import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { AgentConnection } from '@cef-ai/vault-sdk';
import { parseAnchor, resolveAnchor } from '@/src/data/anchors';
import { pickActiveAnchor } from '@/src/lib/scroll-spy';
import { AgentsPage } from '@/src/components/AgentsPage';
import { DisconnectAgentDialog } from '@/src/components/DisconnectAgentDialog';
import { useConfig } from '@/src/config/context';
import { displayAgentName } from '@/lib/vault/agent-display';
import { useVaultSession } from '@/src/state/session';
import type { VaultLayoutOutlet } from './vault-layout';

export function HomePage() {
  const config = useConfig();
  const session = useVaultSession();
  const { selectedAnchor, onSelectAnchor, orderedAgents, jobs, setActiveAnchor } = useOutletContext<VaultLayoutOutlet>();
  const { wallet, vault, agents, agentCards, scopes, disconnect, delegateGranted, setDelegateActive, agentsLoaded } = session;

  const [disconnectTarget, setDisconnectTarget] = useState<AgentConnection | null>(null);
  const lastActiveRef = useRef<string | null>(null);

  // Scroll-spy: highlight the sidebar tab for whatever block is in view as the
  // user scrolls (not just on click). Watches the .vault-onescroll container,
  // rAF-throttled; only pushes state when the active block actually changes
  // (so it doesn't re-render the layout every frame). Reports up via
  // setActiveAnchor — it drives the highlight, never the scroll effect.
  useEffect(() => {
    const container = document.querySelector('.vault-onescroll');
    if (!(container instanceof HTMLElement)) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const cTop = container.getBoundingClientRect().top;
      const blocks = Array.from(container.querySelectorAll('[data-anchor]')).map((el) => ({
        anchor: el.getAttribute('data-anchor') ?? '',
        top: el.getBoundingClientRect().top - cTop,
      }));
      const active = pickActiveAnchor(blocks, container.clientHeight * 0.4);
      if (active !== lastActiveRef.current) {
        lastActiveRef.current = active;
        setActiveAnchor(active);
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update(); // seed the initial highlight
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => { container.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [orderedAgents, setActiveAnchor]);

  // The one-scroll body is a fixed-height, self-scrolling container
  // (.vault-onescroll). Sidebar rows / feed rows set the anchor; we scroll the
  // matching block into that container. Same machinery serves agent blocks and
  // the Scopes/Settings section blocks (data-anchor="section-scopes"/"...settings").
  useEffect(() => {
    // Scroll synchronously (no rAF): the DOM is already committed when this
    // effect runs, and VaultLayout's ~10s poll / order recompute can re-render
    // within a frame — a deferred rAF would get cancelled by the effect
    // cleanup before it fires (observed: scheduled→cancelled→never fired).
    const container = document.querySelector('.vault-onescroll');
    if (!(container instanceof HTMLElement)) return;
    if (selectedAnchor === '' || selectedAnchor === null) {
      container.scrollTo({ top: 0 });
      return;
    }
    // Normalise external deep-links (?anchor=<bare agentId>) into the internal
    // agent anchor before parsing so they scroll like a sidebar click.
    const anchor = resolveAnchor(selectedAnchor, agents.map((a) => a.agentId));
    if (!parseAnchor(anchor)) return;
    const el = document.querySelector(`[data-anchor="${anchor}"]`);
    if (!el) return;
    // Compute the target scrollTop from bounding rects. Instant, not smooth:
    // `scroll-snap-type: y mandatory` cancels a smooth programmatic scroll to a
    // distant snap point in Chrome (silent no-op); instant lands reliably and
    // the snap settles it. scrollIntoView is also flaky inside a snap container.
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTo({ top });
  }, [selectedAnchor, agents]);

  return (
    <>
      <AgentsPage
        agents={orderedAgents}
        agentCards={agentCards}
        scopes={scopes}
        jobs={jobs}
        agentsLoaded={agentsLoaded}
        signer={wallet.signingSigner}
        onSelectAnchor={onSelectAnchor}
        settingsWallet={wallet}
        vaultId={vault.vaultId}
        vaultBaseUrl={config.vaultBaseUrl}
        // NOT `vault.status !== 'disconnected'`. That is the CREDENTIAL's
        // status — 'active' whenever vault-api minted a working credential
        // during `ensure` — and on a resumed session it is independent of
        // whether the write-delegation proxy that lets vault-api RENEW it is
        // known to this device. `=== true` so unknown reads as unconfirmed,
        // never confirmed.
        delegateActive={delegateGranted === true}
        onDelegateChange={setDelegateActive}
        onDisconnect={disconnect}
        onDisconnectAgent={setDisconnectTarget}
        marketplaceBaseUrl={config.marketplaceBaseUrl}
      />

      {disconnectTarget && (
        <DisconnectAgentDialog
          open
          agentId={disconnectTarget.agentId}
          agentName={agentCards[disconnectTarget.agentId]?.name ?? displayAgentName(disconnectTarget.agentId)}
          onClose={() => setDisconnectTarget(null)}
        />
      )}

    </>
  );
}
